import { z } from "zod";

/**
 * Collapse a ZodError into its most specific leaf issues.
 *
 * Zod reports a failed `z.union(...)` as a single `invalid_union` issue whose
 * message is the unhelpful "Invalid input"; the real detail lives in
 * `unionErrors`. This descends every union and keeps the deepest member issues
 * at each union node so callers can render path-aware validation errors.
 */
export function collectLeafZodIssues(error: z.ZodError): z.ZodIssue[] {
  const leaves: z.ZodIssue[] = [];

  for (const issue of error.issues) {
    if (issue.code === z.ZodIssueCode.invalid_union) {
      const memberIssues = issue.unionErrors.flatMap((memberError) =>
        collectLeafZodIssues(memberError),
      );

      if (memberIssues.length === 0) {
        continue;
      }

      const maxDepth = memberIssues.reduce(
        (depth, memberIssue) => Math.max(depth, memberIssue.path.length),
        0,
      );

      leaves.push(
        ...memberIssues.filter(
          (memberIssue) => memberIssue.path.length === maxDepth,
        ),
      );
    } else {
      leaves.push(issue);
    }
  }

  return leaves;
}

/**
 * Build a `field path -> messages` map from a ZodError. Keys are dotted paths
 * that include array indices (e.g. `messages.2.content`) so the response points
 * at the exact location of the problem. Top-level issues keep their bare field
 * name (e.g. `prompt`) and pathless issues are grouped under `_errors`.
 *
 * Uses {@link collectLeafZodIssues} to descend union errors so the response says
 * what is actually wrong instead of a generic "Invalid input".
 */
export function formatZodErrorFields(
  error: z.ZodError,
): Record<string, string[]> {
  const fields: Record<string, string[]> = {};

  for (const issue of collectLeafZodIssues(error)) {
    const key = issue.path.length > 0 ? issue.path.join(".") : "_errors";
    const messages = (fields[key] ??= []);

    if (!messages.includes(issue.message)) {
      messages.push(issue.message);
    }
  }

  return fields;
}
